import { appConfig } from '../config/app.config';

declare global {
    interface Window {
        gapi: any;
        google: any;
    }
}

// Define types for Google Drive API
export interface GoogleDriveFile {
    id: string;
    name: string;
    mimeType: string;
    size?: number;
    thumbnailLink?: string;
    iconLink?: string;
}

export class GoogleDriveService {
    private static instance: GoogleDriveService;
    private tokenClient: any;
    private accessToken: string | null = null;
    private pickerApiLoaded = false;
    private gisLoaded = false;

    private readonly API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;
    private readonly CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    private readonly SCOPES = 'https://www.googleapis.com/auth/drive.readonly';
    private readonly DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'];

    private initPromise: Promise<void> | null = null;

    private constructor() {
        // No auto-init in constructor, wait for explicit call or usage
    }

    public static getInstance(): GoogleDriveService {
        if (!GoogleDriveService.instance) {
            GoogleDriveService.instance = new GoogleDriveService();
        }
        return GoogleDriveService.instance;
    }

    private loadScript(src: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = (err) => reject(new Error(`Failed to load script ${src}: ${err}`));
            document.head.appendChild(script);
        });
    }

    public async initialize(): Promise<void> {
        if (this.initPromise) return this.initPromise;

        this.initPromise = (async () => {
            console.log('[GoogleDriveService] Starting initialization...');

            // Check for required configuration
            if (!this.API_KEY) {
                console.error('[GoogleDriveService] API Key is missing! Check VITE_GOOGLE_API_KEY environment variable.');
            }
            if (!this.CLIENT_ID) {
                console.error('[GoogleDriveService] Client ID is missing! Check VITE_GOOGLE_CLIENT_ID environment variable.');
                throw new Error("Google Client ID not configured");
            }

            try {
                // 1. Load gapi if not present
                if (!(window as any).gapi) {
                    console.log('[GoogleDriveService] Loading GAPI script...');
                    await this.loadScript('https://apis.google.com/js/api.js');
                }

                // 2. Initialize gapi client and picker
                await new Promise<void>((resolve, reject) => {
                    const checkGapi = () => {
                        if ((window as any).gapi) {
                            (window as any).gapi.load('client:picker', async () => {
                                try {
                                    console.log('[GoogleDriveService] Initializing GAPI client...');
                                    await (window as any).gapi.client.init({
                                        apiKey: this.API_KEY,
                                        discoveryDocs: this.DISCOVERY_DOCS,
                                    });
                                    this.pickerApiLoaded = true;
                                    console.log('[GoogleDriveService] Google Picker API loaded successfully');
                                    resolve();
                                } catch (err) {
                                    console.error('[GoogleDriveService] GAPI client init failed:', err);
                                    reject(err);
                                }
                            });
                        } else {
                            setTimeout(checkGapi, 100);
                        }
                    };
                    checkGapi();
                });

                // 3. Load GIS if not present
                if (!(window as any).google?.accounts) {
                    console.log('[GoogleDriveService] Loading GIS script...');
                    await this.loadScript('https://accounts.google.com/gsi/client');
                }

                // 4. Initialize GIS logic
                // Wait for google.accounts to be available
                await new Promise<void>((resolve) => {
                    const checkGis = () => {
                        if ((window as any).google?.accounts) {
                            resolve();
                        } else {
                            setTimeout(checkGis, 100);
                        }
                    };
                    checkGis();
                });

                console.log('[GoogleDriveService] Initializing Token Client...');
                this.tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
                    client_id: this.CLIENT_ID,
                    scope: this.SCOPES,
                    callback: (response: any) => {
                        if (response.error !== undefined) {
                            console.error("[GoogleDriveService] Token client error callback:", response);
                            throw response;
                        }
                        this.accessToken = response.access_token;
                        console.log('[GoogleDriveService] Access token received');
                    },
                    ux_mode: 'popup',
                });
                this.gisLoaded = true;
                console.log('[GoogleDriveService] Google Identity Services loaded successfully');

            } catch (error) {
                console.error("[GoogleDriveService] Initialization sequence failed:", error);
                this.initPromise = null; // Allow retry
                throw error;
            }
        })();

        return this.initPromise;
    }

    // Request access token
    private async requestAccessToken(): Promise<string> {
        await this.initialize();

        return new Promise((resolve, reject) => {
            if (!this.tokenClient) {
                reject(new Error("Google Identity Services not initialized."));
                return;
            }

            const originalCallback = this.tokenClient.callback;

            this.tokenClient.callback = (response: any) => {
                if (response.error !== undefined) {
                    reject(response);
                } else {
                    this.accessToken = response.access_token;
                    resolve(this.accessToken!);
                }
                this.tokenClient.callback = originalCallback;
            };

            if (this.accessToken === null) {
                this.tokenClient.requestAccessToken({ prompt: '' });
            } else {
                this.tokenClient.requestAccessToken({ prompt: '' });
            }
        });
    }

    // Open the Google Picker
    public async openPicker(): Promise<{ file: GoogleDriveFile, accessToken: string } | null> {
        await this.initialize();

        if (!this.pickerApiLoaded || !this.gisLoaded) {
            throw new Error('Google APIs failed to load.');
        }

        // Ensure we have an access token
        let token = this.accessToken;
        if (!token) {
            token = await this.requestAccessToken();
        }

        return new Promise((resolve, reject) => {
            const view = new (window as any).google.picker.View((window as any).google.picker.ViewId.DOCS);
            view.setMimeTypes('application/vnd.google-apps.document,application/vnd.google-apps.spreadsheet,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain');

            const picker = new (window as any).google.picker.PickerBuilder()
                .enableFeature((window as any).google.picker.Feature.NAV_HIDDEN)
                .enableFeature((window as any).google.picker.Feature.MULTISELECT_ENABLED)
                .setAppId(this.CLIENT_ID)
                .setOAuthToken(token)
                .addView(view)
                .setCallback((data: any) => {
                    if (data[google.picker.Response.ACTION] === google.picker.Action.PICKED) {
                        const doc = data[google.picker.Response.DOCUMENTS][0];
                        resolve({
                            file: {
                                id: doc[google.picker.Document.ID],
                                name: doc[google.picker.Document.NAME],
                                mimeType: doc[google.picker.Document.MIME_TYPE],
                                size: doc.sizeBytes,
                                iconLink: doc[google.picker.Document.ICON_URL]
                            },
                            accessToken: token!
                        });
                    } else if (data[google.picker.Response.ACTION] === google.picker.Action.CANCEL) {
                        resolve(null);
                    }
                })
                .build();
            picker.setVisible(true);

            // Fix z-index issue where picker appears behind MUI/Radix Dialog
            const style = document.createElement('style');
            style.innerHTML = `
                .picker-dialog {
                    z-index: 100000 !important;
                    pointer-events: auto !important;
                }
                .picker-dialog-bg,
                .picker-modal-dialog,
                .picker-modal-dialog-bg {
                    z-index: 99999 !important;
                    pointer-events: auto !important;
                }
            `;
            document.head.appendChild(style);
        });
    }
}
